import { resolveVersion } from '@synanetics/secrets';

/**
 * A function responsible for handling the parsing of the supplied environment variable.
 */
export type ParserFunction<T> = (value?: string) => T;

/**
 * Simple options to determine how the supplied environment variable should be handled.
 */
export type ParsingOptions = {
  type: 'string' | 'number' | 'URL' | 'object' | 'boolean';
  defaultValue?: string | number | URL | object | boolean;
};

/**
 * A type describing how to handle and individual environment variable.
 */
export type EnvVariable<T> = {
  required?: boolean;
  parser: ParsingOptions | ParserFunction<T>;
};

/**
 * A type acting as a dictionary for the supplied `EnvVariable` configurations, where the keys
 * correspond to the environment variable names.
 */
export type EnvConfig<T extends Record<string, any>> = Record<keyof T, EnvVariable<T[keyof T]>>;

export type ResultsType<T extends Record<string, any>> = T;

/**
 * The output of the parsing operation.
 */
export type EnvParsingResult<T extends Record<string, any>> = {
  /**
   * A boolean indicating the overall status of the parsing operations.
   */
  success: boolean;
  /**
   * The results of the parsing operations, keyed on the name of the environment variable.
   */
  result: ResultsType<T>;
  /**
   * An array of all of the errors that occurred during parsing.
   */
  errors: Error[];
};

const getTypeError = (key: string, value: string, type: string): Error => new Error(`Variable "${key}" with value "${value}" is not a valid ${type}.`);

const parseValue = async <T extends Record<string, any>>(
  envKey: string,
  envConfig: EnvVariable<T>,
  sourceValue: string | undefined,
): Promise<[keyof T, any]> => {
  let targetValue;

  if (!sourceValue && envConfig.required && (typeof envConfig.parser !== 'function' && !envConfig.parser.defaultValue)) {
    throw new Error(`Required variable "${envKey}" has not been defined.`);
  }

  if (typeof envConfig.parser === 'function') {
    try {
      targetValue = envConfig.parser(sourceValue);
    } catch (ex) {
      throw ex as Error;
    }
  } else if (sourceValue != null) {
    let intermediateValue: string | undefined;

    try {
      intermediateValue = await resolveVersion(sourceValue);
    } catch (ex) {
      throw new Error(`Could not resolve "${sourceValue}" from variable "${envKey}".`, { cause: ex });
    }

    switch (envConfig.parser.type) {
      case 'URL':
        if (URL.canParse(intermediateValue)) {
          targetValue = new URL(intermediateValue);
        } else {
          throw getTypeError(envKey, intermediateValue, envConfig.parser.type);
        }
        break;
      case 'boolean':
        if (['TRUE', '1'].includes(intermediateValue.toUpperCase())) {
          targetValue = true;
        } else if (['FALSE', '0'].includes(intermediateValue.toUpperCase())) {
          targetValue = false;
        } else throw getTypeError(envKey, intermediateValue, envConfig.parser.type);
        break;
      case 'number':
        // eslint-disable-next-line no-case-declarations
        const num = Number(intermediateValue);
        if (Number.isNaN(num) || !sourceValue?.trim()) {
          throw getTypeError(envKey, intermediateValue, envConfig.parser.type);
        }
        targetValue = num;
        break;
      case 'object':
        try {
          targetValue = JSON.parse(intermediateValue);
        } catch (ex) {
          throw getTypeError(envKey, intermediateValue, envConfig.parser.type);
        }
        break;
      case 'string':
        targetValue = intermediateValue;
        break;
      default:
        throw new Error(`Variable type "${envConfig.parser.type}" is not valid.`);
    }
  }

  if (targetValue == null && typeof envConfig.parser !== 'function') {
    targetValue = envConfig.parser.defaultValue;
  }

  return [envKey, targetValue];
};

/**
 * Processes the supplied environment variables (defaults to `process.env`), according to the
 * supplied configuration, and returns a dictionary of the typed variables, a list of any errors
 * that occurred, and an overall status.
 * @param config The `EnvConfig`.
 * @param env The variables to parse. Default: `process.env`.
 * @returns An `EnvParsingResult`, describing the parsed variables.
 */
const processEnvParser = async <T extends Record<string, any>>(
  config: EnvConfig<T>,
  env: Record<string, string | undefined> = process.env,
): Promise<EnvParsingResult<T>> => {
  const res = {} as T;
  const result: EnvParsingResult<T> = {
    success: true,
    result: res,
    errors: [],
  };

  const parsingResults = await Promise.allSettled(
    Object.entries(config).map((e) => parseValue<T>(e[0], e[1], env[e[0]])),
  );

  parsingResults.forEach((r) => {
    if (r.status === 'rejected') {
      result.success = false;
      result.errors.push(r.reason);
      return;
    }
    const [key, value] = r.value;
    result.result[key] = value;
  });

  return result;
};

export default processEnvParser;
